import { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, Home as HomeIcon, Compass, MessageSquare, User, Settings, Crown, LogOut, Calendar, Bell, UserPlus, Sofa, Shield, Phone, History, Users } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const { user, logout, token } = useContext(AuthContext);
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [subStatus, setSubStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchNotifications();
      fetchSubStatus();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchSubStatus = async () => {
    try {
      const res = await axios.get(`${API}/payments/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubStatus(res.data);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const markAsRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Helper function to check if a path is active
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    if (path === '/messages') {
      return location.pathname.startsWith('/messages');
    }
    return location.pathname === path;
  };

  return (
    <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div
            className="flex items-center space-x-2 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            <Globe className="w-7 h-7 text-coral" />
            <h1 className="text-xl font-bold hidden sm:block">CultraMeet</h1>
          </div>

          {/* Navigation */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              data-testid="nav-home"
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Home"
            >
              <HomeIcon className={`w-6 h-6 ${isActive('/dashboard') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-discover"
              onClick={() => navigate('/discover')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Discover"
            >
              <Compass className={`w-6 h-6 ${isActive('/discover') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-events"
              onClick={() => navigate('/events')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Events"
            >
              <Calendar className={`w-6 h-6 ${isActive('/events') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-couch-surfing"
              onClick={() => navigate('/couch-surfing')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
              title="Couch Surfing"
            >
              <Sofa className={`w-6 h-6 ${isActive('/couch-surfing') ? 'text-teal' : 'text-gray-700'}`} />
              {user?.is_premium && (
                <span className="absolute top-0 right-0 w-2 h-2 bg-gold rounded-full"></span>
              )}
            </button>
            <button
              data-testid="nav-group-calls"
              onClick={() => navigate('/group-calls')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Group Calls"
            >
              <Phone className={`w-6 h-6 ${isActive('/group-calls') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-call-history"
              onClick={() => navigate('/call-history')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Call History"
            >
              <History className={`w-6 h-6 ${isActive('/call-history') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-messages"
              onClick={() => navigate('/messages')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Messages"
            >
              <MessageSquare className={`w-6 h-6 ${isActive('/messages') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-groups"
              onClick={() => navigate('/groups')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title="Group Chats"
            >
              <Users className={`w-6 h-6 ${isActive('/groups') ? 'text-teal' : 'text-gray-700'}`} />
            </button>
            <button
              data-testid="nav-invite"
              onClick={() => navigate('/invite')}
              className="p-2 rounded-full hover:bg-teal-50 transition-colors border-2 border-teal"
              title="Invite Friends"
            >
              <UserPlus className="w-6 h-6 text-black" />
            </button>

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="notifications-trigger" className="relative p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none">
                <Bell className="w-6 h-6 text-gray-700" />
                {unreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-coral text-white text-xs border-none">
                    {unreadCount}
                  </Badge>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-white rounded-2xl shadow-xl max-h-96 overflow-y-auto">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold">Notifications</h3>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-coral hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500">
                    <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No notifications yet</p>
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      data-testid={`notification-${notif.id}`}
                      onClick={() => {
                        if (!notif.read) markAsRead(notif.id);
                        if (notif.link) navigate(notif.link);
                      }}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b ${
                        !notif.read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <p className="text-sm">{notif.content}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="focus:outline-none">
                <Avatar className="cursor-pointer border-4 border-teal shadow-lg">
                  <AvatarImage src={user?.profile_photo} />
                  <AvatarFallback className="bg-white text-black font-bold text-lg border-2 border-teal">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white rounded-2xl shadow-xl">
                <div className="px-4 py-3 border-b">
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  {user?.is_premium && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-1">
                        <Crown className="w-4 h-4 text-gold" />
                        <span className="text-xs font-semibold text-gold">Premium</span>
                      </div>
                      {subStatus?.days_left != null && (
                        <div className="mt-1">
                          {subStatus.days_left <= 7 ? (
                            <span
                              data-testid="sub-renew-warning"
                              className="text-xs text-red-500 font-medium cursor-pointer hover:underline"
                              onClick={() => navigate('/subscription')}
                            >
                              Expires in {subStatus.days_left} day{subStatus.days_left !== 1 ? 's' : ''} — Renew
                            </span>
                          ) : subStatus.days_left <= 30 ? (
                            <span data-testid="sub-days-left" className="text-xs text-yellow-600">
                              {subStatus.days_left} days remaining
                            </span>
                          ) : (
                            <span data-testid="sub-days-left" className="text-xs text-gray-500">
                              {subStatus.days_left} days remaining
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {!user?.is_premium && (
                    <span
                      data-testid="sub-upgrade-link"
                      className="text-xs text-coral font-medium mt-1 cursor-pointer hover:underline block"
                      onClick={() => navigate('/subscription')}
                    >
                      Upgrade to Premium
                    </span>
                  )}
                </div>
                <DropdownMenuItem
                  data-testid="menu-profile"
                  onClick={() => navigate('/profile')}
                  className={`cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-lg ${isActive('/profile') ? 'font-bold' : ''}`}
                >
                  <User className={`w-4 h-4 mr-2 ${isActive('/profile') ? 'text-black' : ''}`} />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-subscription"
                  onClick={() => navigate('/subscription')}
                  className={`cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-lg ${isActive('/subscription') ? 'font-bold' : ''}`}
                >
                  <Crown className={`w-4 h-4 mr-2 ${isActive('/subscription') ? 'text-black' : ''}`} />
                  {user?.is_premium ? 'Manage Subscription' : 'Upgrade to Premium'}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="menu-settings"
                  onClick={() => navigate('/settings')}
                  className={`cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-lg ${isActive('/settings') ? 'font-bold' : ''}`}
                >
                  <Settings className={`w-4 h-4 mr-2 ${isActive('/settings') ? 'text-black' : ''}`} />
                  Settings
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem
                    data-testid="menu-admin"
                    onClick={() => navigate('/admin')}
                    className="cursor-pointer px-4 py-2 hover:bg-gray-100 rounded-lg"
                  >
                    <Shield className="w-4 h-4 mr-2 text-coral" />
                    Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onClick={handleLogout}
                  className="cursor-pointer px-4 py-2 hover:bg-red-50 text-red-600 rounded-lg"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;